import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Book } from '@booknest/shared';

import {
  Bookcase,
  type BookcaseUnitData,
} from '@/features/library/components/bookcase/Bookcase.js';
import { BookModal } from '@/features/library/components/BookModal.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';

import { CollectionFormModal } from '../components/CollectionFormModal.js';
import { useCollectionDetails } from '../hooks/useMyCollections.js';

export function CollectionDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useCollectionDetails(id ?? null);
  const { data: shelfData } = useMyShelf();
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [openingBookId, setOpeningBookId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  if (!id) {
    navigate('/library');
    return <></>;
  }

  if (isLoading) {
    return <p className="text-sm text-brown-soft">Chargement…</p>;
  }
  if (!data) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-brown-soft">Collection introuvable.</p>
        <button
          type="button"
          onClick={() => navigate('/library')}
          className="self-start rounded-full bg-paper px-4 py-2 text-sm font-semibold text-brown ring-1 ring-cream-dark hover:bg-cream-dark"
        >
          ← Retour à mes étagères
        </button>
      </div>
    );
  }

  const collection = data.collection;
  const books: ReadonlyArray<Book> = collection.books;
  const selectedBook = books.find((b) => b.id === selectedBookId) ?? null;
  const selectedEntry =
    shelfData?.items.find((e) => e.book.id === selectedBookId) ?? null;

  const handleBookClick = (bookId: string): void => {
    if (openingBookId !== null) return;
    setOpeningBookId(bookId);
    window.setTimeout(() => setSelectedBookId(bookId), 380);
  };

  const handleClose = (): void => {
    setSelectedBookId(null);
    setOpeningBookId(null);
  };

  const unit: BookcaseUnitData = {
    key: collection.id,
    label: collection.name,
    emoji: collection.emoji,
    emptyMessage: 'Cette collection est encore vide',
    firstAsCover: true,
    books: [...books],
  };

  return (
    <div className="flex flex-col gap-5">
      <header className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/library')}
          aria-label="Retour"
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-paper text-brown shadow-sm hover:bg-cream-dark"
        >
          ←
        </button>
        <div className="flex-1">
          <h1 className="font-serif text-2xl font-semibold text-brown md:text-3xl">
            <span className="mr-1.5" aria-hidden>
              {collection.emoji}
            </span>
            {collection.name}
          </h1>
          <p className="font-script text-base text-brown-soft">
            {collection.description ? `${collection.description} · ` : ''}
            {collection.bookIds.length} livre
            {collection.bookIds.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Modifier"
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-paper text-brown shadow-sm hover:bg-cream-dark"
        >
          ✏️
        </button>
      </header>

      <Bookcase
        units={[unit]}
        openingBookId={openingBookId}
        onBookClick={(b) => handleBookClick(b.id)}
      />

      <p className="text-center font-script text-base text-brown-soft">
        👆 touche une tranche pour ouvrir le livre
      </p>

      {selectedBook && (
        <BookModal
          book={selectedBook}
          entry={selectedEntry}
          onClose={handleClose}
        />
      )}

      {editing && (
        <CollectionFormModal
          existingId={collection.id}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}
